package top.wcpe.allincore.ui.web.model

import top.wcpe.allincore.ui.web.asset.AssetObjectUrlBackend
import top.wcpe.allincore.ui.web.asset.AssetPathPolicy
import top.wcpe.allincore.ui.web.asset.AssetReferenceKind
import top.wcpe.allincore.ui.web.asset.ObjectUrlLease
import top.wcpe.allincore.ui.web.asset.PreviewAssetResolver
import kotlin.js.Promise

internal data class ThreeModelAsset(
    val canonicalPath: String,
    val contentVersion: String,
    val text: String,
)

internal interface ThreeTextureUrlLease {
    val url: String
    fun dispose()
}

internal interface ThreeModelAssetLoader {
    fun loadModel(modelBase: String, source: String): Promise<ThreeModelAsset>

    fun loadTexture(
        modelAsset: ThreeModelAsset,
        texture: RestrictedBbTexture,
    ): Promise<ThreeTextureUrlLease>

    fun dispose() = Unit
}

internal class ResolverThreeModelAssetLoader(
    private val resolver: PreviewAssetResolver,
    private val objectUrls: AssetObjectUrlBackend,
) : ThreeModelAssetLoader {
    private var disposed = false

    override fun loadModel(modelBase: String, source: String): Promise<ThreeModelAsset> {
        if (disposed) return Promise.reject(IllegalStateException("模型资产加载器已释放"))
        val canonicalPath = try {
            AssetPathPolicy.relative(modelBase, source.removePrefix("./"))
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        return resolver.resolveCanonical(canonicalPath, AssetReferenceKind.MODEL).then { asset ->
            ThreeModelAsset(asset.canonicalPath, asset.contentVersion, asset.content.decodeToString())
        }
    }

    override fun loadTexture(
        modelAsset: ThreeModelAsset,
        texture: RestrictedBbTexture,
    ): Promise<ThreeTextureUrlLease> {
        if (disposed) return Promise.reject(IllegalStateException("模型资产加载器已释放"))
        return when (val source = texture.source) {
            is RestrictedBbExternalTexture -> loadExternalTexture(source)
            is RestrictedBbInlineTexture -> loadInlineTexture(source)
        }
    }

    override fun dispose() {
        if (disposed) return
        resolver.dispose()
        disposed = true
    }

    private fun loadExternalTexture(source: RestrictedBbExternalTexture): Promise<ThreeTextureUrlLease> =
        resolver.resolveCanonical(source.request.canonicalPath, AssetReferenceKind.TEXTURE).then { asset ->
            ResolverTextureUrlLease(resolver.acquireObjectUrl(asset))
        }

    private fun loadInlineTexture(source: RestrictedBbInlineTexture): Promise<ThreeTextureUrlLease> = try {
        Promise.resolve(InlineTextureUrlLease(objectUrls.create(source.bytes, source.mime), objectUrls))
    } catch (error: Throwable) {
        Promise.reject(error)
    }
}

internal fun interface ThreeLeaseRetryScheduler {
    fun schedule(task: () -> Unit)
}

private object GlobalThreeLeaseRetryScheduler : ThreeLeaseRetryScheduler {
    override fun schedule(task: () -> Unit) {
        js("globalThis.setTimeout(task, 0)")
    }
}

internal class ResolverTextureUrlLease(
    private val delegate: ObjectUrlLease,
    scheduler: ThreeLeaseRetryScheduler = GlobalThreeLeaseRetryScheduler,
) : RetryingTextureUrlLease(scheduler) {
    override val url: String
        get() = delegate.url

    override fun release(): Boolean = delegate.tryDispose()
}

internal class InlineTextureUrlLease(
    override val url: String,
    private val objectUrls: AssetObjectUrlBackend,
    scheduler: ThreeLeaseRetryScheduler = GlobalThreeLeaseRetryScheduler,
) : RetryingTextureUrlLease(scheduler) {
    override fun release(): Boolean {
        objectUrls.revoke(url)
        return true
    }
}

internal abstract class RetryingTextureUrlLease(
    private val scheduler: ThreeLeaseRetryScheduler,
) : ThreeTextureUrlLease {
    private var released = false
    private var retryScheduled = false

    final override fun dispose() {
        if (released || retryScheduled) return
        attemptRelease()
    }

    protected abstract fun release(): Boolean

    private fun attemptRelease() {
        val succeeded = runCatching(::release).getOrDefault(false)
        if (succeeded) {
            released = true
            return
        }
        retryScheduled = true
        scheduler.schedule(::retry)
    }

    private fun retry() {
        retryScheduled = false
        if (!released) attemptRelease()
    }
}
